import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, KeyRound, Save, ShieldCheck, Trash2, UserRound } from "lucide-react";
import { api, errMsg, passwordScore } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Loader, Pill } from "@/components/Bits";
import { PasswordMeter } from "@/components/PasswordMeter";
import { useAuth } from "@/context/AuthContext";

const field = "w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:border-blue-500 transition";

const SITUATIONS = [
  ["in_school", "In school"],
  ["homeschooled", "Learning at home"],
  ["never", "Never been to school"],
  ["break", "On a break"],
  ["finished", "Finished school"],
  ["unsure", "Rather not say"],
];
const COMFORT = [["confident", "Confident"], ["okay", "Okay"], ["shaky", "Shaky"], ["never", "Never studied it"]];
const AREAS = [["maths", "Maths"], ["science", "Science"], ["computing", "Computing"]];
const BARRIERS = ["Nothing, I'm good", "My attention wanders", "Reading is slow for me", "Maths makes me anxious", "Exams make me nervous", "Dyslexia or ADHD"];
const STYLES = ["Worked examples", "Pictures and diagrams", "Stories", "Hands-on", "Small steps", "Talking it through"];
const INTERESTS = ["Space", "Games", "Football", "Music", "Animals", "Cars", "Robots", "Drawing", "Cooking", "History", "Money", "Nature"];
const GOALS = [
  ["catch_up", "Catch up"], ["keep_up", "Keep up with school"], ["go_deeper", "Go deeper"],
  ["board_exam", "Prepare for an exam"], ["career_explore", "Explore a career"], ["build", "Build something"], ["curiosity", "Just curious"],
];

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
      active ? "bg-slate-900 border-slate-900 text-white" : "border-slate-300 text-slate-700 hover:border-slate-500"
    }`}
  >
    {children}
  </button>
);

const Section = ({ title, note, children, testId }) => (
  <div className="card-flat p-7" data-testid={testId}>
    <h2 className="font-display text-xl font-bold">{title}</h2>
    {note && <p className="mt-1 text-sm text-slate-500">{note}</p>}
    <div className="mt-5 space-y-5">{children}</div>
  </div>
);

const toggle = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

export default function Profile() {
  const { user, refresh } = useAuth();
  const [p, setP] = useState(null);
  const [usage, setUsage] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [profile, u, g] = await Promise.all([api.get("/profile"), api.get("/usage"), api.get("/profile/guardian")]);
    setP({ ...profile.data, schooling: profile.data.schooling || {}, goal: profile.data.goal || {}, interests: profile.data.interests || [] });
    setUsage(u.data);
    setGuardian(g.data);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const save = async (patch, label = "Saved") => {
    setBusy(label);
    try {
      const { data } = await api.patch("/profile", patch);
      setP({ ...data, schooling: data.schooling || {}, goal: data.goal || {}, interests: data.interests || [] });
      await refresh();
      toast.success(label);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const changePassword = async () => {
    setBusy("password");
    try {
      const { data } = await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      localStorage.setItem("es_token", data.token);
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success(data.message);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const invite = async () => {
    try {
      const { data } = await api.post("/profile/guardian", { guardian_email: inviteEmail });
      setInviteLink(data.verification_link);
      setInviteEmail("");
      await load();
      toast.success("Guardian invited");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const unlink = async () => {
    if (!window.confirm("Remove your guardian? Your plan and progress are untouched.")) return;
    await api.delete("/profile/guardian");
    setInviteLink("");
    await load();
    toast.success("Guardian removed");
  };

  if (!p) return <Shell><Loader label="Loading your profile…" /></Shell>;

  const set = (patch) => setP({ ...p, ...patch });
  const setSchool = (patch) => setP({ ...p, schooling: { ...p.schooling, ...patch } });
  const setGoal = (patch) => setP({ ...p, goal: { ...p.goal, ...patch } });
  const pwStrength = passwordScore(pw.new_password);
  const usedPct = usage ? Math.min(100, Math.round((usage.used_today / usage.daily_cap) * 100)) : 0;

  return (
    <Shell>
      <div className="max-w-4xl reveal space-y-6" data-testid="profile-root">
        <div>
          <p className="rule-label">Your account</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold">Profile</h1>
          <p className="mt-2 text-sm text-slate-500">
            Everything you told us at the start, editable. Changing your grade or how confident you feel changes what the
            advisor drafts next.
          </p>
        </div>

        <Section title="About you" testId="profile-about">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="rule-label">Name</label>
              <input data-testid="profile-name" className={`${field} mt-2`} value={p.name || ""} onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div>
              <label className="rule-label">Date of birth</label>
              <input data-testid="profile-dob" type="date" className={`${field} mt-2`} value={(p.dob || "").slice(0, 10)} onChange={(e) => set({ dob: e.target.value })} />
              <p className="mt-1.5 text-xs text-slate-500">
                {p.age ? `${p.age} years old` : "—"}
                {p.recommended_grade ? ` · usual grade for this age: ${p.recommended_grade}` : ""}
              </p>
            </div>
            <div>
              <label className="rule-label">Grade you're working at</label>
              <select data-testid="profile-grade" className={`${field} mt-2`} value={p.grade || ""} onChange={(e) => set({ grade: e.target.value })}>
                <option value="">Not sure</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i} value={String(i + 1)}>Grade {i + 1}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="rule-label">Country</label>
              <input data-testid="profile-country" className={`${field} mt-2`} value={p.country || ""} onChange={(e) => set({ country: e.target.value })} />
            </div>
          </div>
          <button data-testid="profile-save-about" onClick={() => save({ name: p.name, dob: p.dob, grade: p.grade, country: p.country }, "Details saved")} disabled={!!busy} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors">
            <Save className="w-4 h-4" /> Save details
          </button>
        </Section>

        <Section title="Where you're at" note="Re-rate these whenever they change — the advisor uses them for depth." testId="profile-level">
          <div>
            <label className="rule-label">Schooling</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {SITUATIONS.map(([key, label]) => (
                <Chip key={key} testId={`profile-situation-${key}`} active={p.schooling.situation === key} onClick={() => setSchool({ situation: key })}>{label}</Chip>
              ))}
            </div>
          </div>
          <div>
            <label className="rule-label">How these feel</label>
            <div className="mt-3 space-y-3">
              {AREAS.map(([key, label]) => (
                <div key={key} className="flex flex-wrap items-center gap-2">
                  <span className="w-24 text-sm font-medium text-slate-700">{label}</span>
                  {COMFORT.map(([value, text]) => (
                    <Chip key={value} testId={`profile-comfort-${key}-${value}`} active={(p.schooling.comfort || {})[key] === value} onClick={() => setSchool({ comfort: { ...(p.schooling.comfort || {}), [key]: value } })}>
                      {text}
                    </Chip>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="rule-label">What makes learning harder (private)</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {BARRIERS.map((b) => (
                <Chip key={b} testId={`profile-barrier-${b.slice(0, 6)}`} active={(p.schooling.barriers || []).includes(b)} onClick={() => setSchool({ barriers: toggle(p.schooling.barriers || [], b) })}>{b}</Chip>
              ))}
            </div>
          </div>
          <div>
            <label className="rule-label">You learn best with</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {STYLES.map((s) => (
                <Chip key={s} testId={`profile-style-${s.slice(0, 6)}`} active={(p.schooling.learning_style || []).includes(s)} onClick={() => setSchool({ learning_style: toggle(p.schooling.learning_style || [], s) })}>{s}</Chip>
              ))}
            </div>
          </div>
          <button data-testid="profile-save-level" onClick={() => save({ schooling: p.schooling }, "Level saved")} disabled={!!busy} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors">
            <Save className="w-4 h-4" /> Save
          </button>
        </Section>

        <Section title="Goal and pace" testId="profile-goal">
          <div>
            <label className="rule-label">What brings you here</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {GOALS.map(([key, label]) => (
                <Chip key={key} testId={`profile-goal-${key}`} active={p.goal.type === key} onClick={() => setGoal({ type: key })}>{label}</Chip>
              ))}
            </div>
          </div>
          <div>
            <label className="rule-label">In your own words</label>
            <textarea data-testid="profile-goal-detail" rows={3} className={`${field} mt-2`} value={p.goal.detail || ""} onChange={(e) => setGoal({ detail: e.target.value })} />
          </div>
          <div>
            <label className="rule-label">What you're into</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {INTERESTS.map((i) => (
                <Chip key={i} testId={`profile-interest-${i}`} active={p.interests.includes(i)} onClick={() => set({ interests: toggle(p.interests, i) })}>{i}</Chip>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="rule-label">Days a week</label>
              <select data-testid="profile-days" className={`${field} mt-2`} value={p.days_per_week || 5} onChange={(e) => set({ days_per_week: Number(e.target.value) })}>
                {[2, 3, 4, 5, 6, 7].map((d) => <option key={d} value={d}>{d} days</option>)}
              </select>
            </div>
            <div>
              <label className="rule-label">Minutes a day</label>
              <select data-testid="profile-minutes" className={`${field} mt-2`} value={p.daily_topic_minutes || 30} onChange={(e) => set({ daily_topic_minutes: Number(e.target.value) })}>
                {[15, 20, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{m} minutes</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="rule-label">When you're stuck</label>
            <div className="mt-3 flex flex-wrap gap-2">
              <Chip testId="profile-stuck-questions" active={p.stuck_pref === "questions"} onClick={() => set({ stuck_pref: "questions" })}>Ask me questions</Chip>
              <Chip testId="profile-stuck-explain" active={p.stuck_pref === "explain"} onClick={() => set({ stuck_pref: "explain" })}>Just explain it</Chip>
            </div>
          </div>
          <button data-testid="profile-save-goal" onClick={() => save({ goal: p.goal, interests: p.interests, days_per_week: p.days_per_week, daily_topic_minutes: p.daily_topic_minutes, stuck_pref: p.stuck_pref }, "Goal saved")} disabled={!!busy} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors">
            <Save className="w-4 h-4" /> Save
          </button>
        </Section>

        <Section title="Today's AI allowance" note="Unbound is free. A daily limit is what keeps it that way." testId="profile-usage">
          <div className="flex items-center gap-3">
            <Gauge className="w-5 h-5 text-blue-600" />
            <div className="flex-1">
              <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                <div className={`h-full ${usedPct > 85 ? "bg-amber-500" : "bg-blue-600"}`} style={{ width: `${usedPct}%` }} />
              </div>
              <p className="mt-2 text-sm text-slate-500" data-testid="profile-usage-text">
                {usage.used_today} of {usage.daily_cap} generations used today · {usage.used_this_month} of {usage.monthly_cap} this month. Resets at midnight UTC.
              </p>
            </div>
          </div>
        </Section>

        <Section title="Guardian" note="Optional, always. Add one, replace one, or remove one." testId="profile-guardian">
          {guardian?.linked ? (
            <div className="flex flex-wrap items-center gap-3">
              <UserRound className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium" data-testid="profile-guardian-email">{guardian.email}</span>
              <Pill tone={guardian.verified ? "green" : "amber"}>{guardian.verified ? "verified" : "invited"}</Pill>
              <button data-testid="profile-guardian-remove" onClick={unlink} className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="profile-guardian-none">No guardian linked. Your plan needs nobody's approval.</p>
          )}
          <div className="flex flex-wrap gap-2">
            <input data-testid="profile-guardian-input" type="email" className={`${field} flex-1 min-w-[240px]`} value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="guardian@email.com" />
            <button data-testid="profile-guardian-invite" onClick={invite} disabled={!inviteEmail.trim()} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors">
              <ShieldCheck className="w-4 h-4" /> {guardian?.linked ? "Replace guardian" : "Invite guardian"}
            </button>
          </div>
          {inviteLink && (
            <p className="text-xs text-slate-500 break-all" data-testid="profile-guardian-link">
              QA mode — share this link: <span className="font-semibold text-blue-700">{inviteLink}</span>
            </p>
          )}
        </Section>

        <Section title="Security" note={`Signed in as ${user?.email || ""}`} testId="profile-security">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="rule-label">Current password</label>
              <input data-testid="profile-current-password" type="password" className={`${field} mt-2`} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
            </div>
            <div>
              <label className="rule-label">New password</label>
              <input data-testid="profile-new-password" type="password" className={`${field} mt-2`} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
            </div>
            <div>
              <label className="rule-label">Confirm new</label>
              <input data-testid="profile-confirm-password" type="password" className={`${field} mt-2`} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
            </div>
          </div>
          <PasswordMeter value={pw.new_password} />
          <button
            data-testid="profile-change-password"
            onClick={changePassword}
            disabled={busy === "password" || !pw.current_password || !pwStrength.valid || pw.new_password !== pw.confirm}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"
          >
            <KeyRound className="w-4 h-4" /> Change password
          </button>
          <p className="text-xs text-slate-400">Changing your password signs you out on every other device.</p>
        </Section>
      </div>
    </Shell>
  );
}
